import { useEffect, useRef } from 'react'
import mapboxgl from 'mapbox-gl'

interface UseFlyToPositionProps {
  map: React.MutableRefObject<mapboxgl.Map | null>
  mapLoaded: boolean
  flyToPosition?: [number, number]
  isFlyingToRef: React.MutableRefObject<boolean>
}

/**
 * Hook to handle flyTo animation with proper maxBounds management
 */
export const useFlyToPosition = ({
  map,
  mapLoaded,
  flyToPosition,
  isFlyingToRef,
}: UseFlyToPositionProps) => {
  const flyToTimeoutRef = useRef<NodeJS.Timeout | null>(null)

  // Handle fly to position
  useEffect(() => {
    if (!map.current || !mapLoaded || !flyToPosition) {
      return
    }

    // Clear any previous timeout
    if (flyToTimeoutRef.current) {
      clearTimeout(flyToTimeoutRef.current)
    }

    // Set flag to prevent bbox from interrupting or re-fitting after flyTo
    isFlyingToRef.current = true

    const currentMaxBounds = map.current.getMaxBounds()

    // Remove maxBounds temporarily to allow flyTo
    if (currentMaxBounds) {
      map.current.setMaxBounds(null)
    }

    // Wait a tick for maxBounds removal to settle before calling flyTo
    setTimeout(() => {
      if (!map.current) return

      // Set up movestart listener first - this ensures we only listen for THIS flyTo's movement
      map.current.once('movestart', () => {
        // Now that the flyTo has started, set up moveend listener for when it completes
        map.current?.once('moveend', () => {
          // Restore maxBounds after flyTo completes
          if (currentMaxBounds) {
            setTimeout(() => {
              if (map.current) {
                map.current.setMaxBounds(currentMaxBounds)
              }
            }, 100)
          }
        })
      })

      map.current.flyTo({
        center: flyToPosition,
        zoom: 17.5, // Zoom closer to show the marker clearly
        duration: 1000, // Smooth animation duration in ms
        essential: true,
      })
    }, 50)

    // Keep flag active for longer to prevent bbox from re-centering after flyTo
    // This gives the user time to see the marker before the map might refit to bbox
    flyToTimeoutRef.current = setTimeout(() => {
      isFlyingToRef.current = false
      flyToTimeoutRef.current = null
    }, 3000) // Keep flag active for 3 seconds

    // Cleanup on unmount
    return () => {
      if (flyToTimeoutRef.current) {
        clearTimeout(flyToTimeoutRef.current)
      }
    }
  }, [flyToPosition, mapLoaded])
}
